import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { analyticsService } from '../../services/api/analyticsService';
import type { DashboardApiResponse } from '../../services/api/types';

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getAdminDashboardStats();
      // API ส่ง data ตรงๆมา ไม่ได้ห่อด้วย success/data
      if (response) {
        setStats(response);
      } else {
        setError('ไม่สามารถโหลดข้อมูลได้');
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; label: string }> = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'ร่าง' },
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'รออนุมัติ' },
      pending_signature: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'รอลงนาม' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'อนุมัติแล้ว' },
      issued: { bg: 'bg-green-100', text: 'text-green-800', label: 'ออกแล้ว' },
      revoked: { bg: 'bg-red-100', text: 'text-red-800', label: 'ยกเลิก' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', label: 'ตรวจสอบแล้ว' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'ส่งแล้ว' },
      generated: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'สร้างแล้ว' },
    };
    return statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
              <p className="font-bold">เกิดข้อผิดพลาด</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={fetchDashboardData}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                ลองอีกครั้ง
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                สวัสดี, {user?.firstName || user?.username || 'ผู้ดูแลระบบ'}
              </h1>
              <p className="text-blue-100">ยินดีต้อนรับสู่ระบบจัดการเกียรติบัตรออนไลน์</p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <p className="text-sm text-blue-100">วันนี้</p>
                <p className="text-xl font-bold">
                  {new Intl.DateTimeFormat('th-TH', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(new Date())}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Certificates Stats */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-green-600">
                +{stats?.certificates?.sent || 0} ส่งแล้ว
              </span>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">เกียรติบัตรทั้งหมด</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.certificates?.total || 0}</p>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                ร่าง: {stats?.certificates?.draft || 0}
              </span>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                สร้างแล้ว: {stats?.certificates?.generated || 0}
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                ตรวจสอบแล้ว: {stats?.certificates?.verified || 0}
              </span>
            </div>
          </div>

          {/* Activities Stats */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 rounded-lg p-3">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-blue-600">
                {stats?.activities?.upcoming || 0} กำลังจะมาถึง
              </span>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">กิจกรรมทั้งหมด</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.activities?.total || 0}</p>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                ดำเนินการ: {stats?.activities?.active || 0}
              </span>
              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                สำเร็จ: {stats?.activities?.completed || 0}
              </span>
            </div>
          </div>

          {/* Users Stats */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 rounded-lg p-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-green-600">
                {stats?.users?.online || 0} ออนไลน์
              </span>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">ผู้ใช้งานทั้งหมด</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.users?.total || 0}</p>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="bg-red-100 text-red-800 px-2 py-1 rounded">
                ผู้ดูแล: {stats?.users?.admins || 0}
              </span>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                เจ้าหน้าที่: {stats?.users?.staff || 0}
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                ผู้ลงนาม: {stats?.users?.signers || 0}
              </span>
            </div>
          </div>

          {/* Verifications Stats */}
          <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-100 rounded-lg p-3">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-green-600">
                {stats?.certificates?.verified || 0} ตรวจสอบแล้ว
              </span>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">การตรวจสอบ</h3>
            <p className="text-3xl font-bold text-gray-900">{stats?.certificates?.verified || 0}</p>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                ส่งแล้ว: {stats?.certificates?.sent || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions & Pending Tasks Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              การดำเนินการด่วน
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/certificates/staff')}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg transition-all transform hover:scale-105 shadow-md flex items-center justify-between"
              >
                <span className="font-medium">จัดการเกียรติบัตร</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => navigate('/activities')}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-3 rounded-lg transition-all transform hover:scale-105 shadow-md flex items-center justify-between"
              >
                <span className="font-medium">จัดการกิจกรรม</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => navigate('/templates')}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-3 rounded-lg transition-all transform hover:scale-105 shadow-md flex items-center justify-between"
              >
                <span className="font-medium">จัดการเทมเพลต</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => navigate('/users')}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-3 rounded-lg transition-all transform hover:scale-105 shadow-md flex items-center justify-between"
              >
                <span className="font-medium">จัดการผู้ใช้</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => navigate('/analytics')}
                className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white px-4 py-3 rounded-lg transition-all transform hover:scale-105 shadow-md flex items-center justify-between"
              >
                <span className="font-medium">รายงานและสถิติ</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Pending Tasks */}
          <div className="bg-white rounded-lg shadow-lg p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                งานที่ต้องดำเนินการ
              </div>
              <span className="bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                {stats?.pendingTasks?.length || 0} รายการ
              </span>
            </h2>

            {stats?.pendingTasks && stats.pendingTasks.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.pendingTasks.map((task) => {
                  const priorityColors: Record<string, { bg: string; text: string }> = {
                    high: { bg: 'bg-red-100', text: 'text-red-800' },
                    medium: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
                    low: { bg: 'bg-green-100', text: 'text-green-800' },
                  };
                  const color = priorityColors[task.priority] || priorityColors.medium;

                  return (
                    <div
                      key={task.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{task.title}</h3>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${color.bg} ${color.text}`}>
                          {task.priority === 'high' ? 'สูง' : task.priority === 'medium' ? 'กลาง' : 'ต่ำ'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          {task.type === 'approval' ? 'อนุมัติ' : 'ตรวจสอบ'}
                        </span>
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(task.dueDate)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">ไม่มีงานที่ต้องดำเนินการ</p>
                <p className="text-sm">คุณได้จัดการทุกอย่างเรียบร้อยแล้ว</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              กิจกรรมล่าสุด
            </div>
            <button
              onClick={() => navigate('/analytics')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              ดูทั้งหมด →
            </button>
          </h2>

          {stats?.recentActivities && stats.recentActivities.length > 0 ? (
            <div className="space-y-3">
              {stats.recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.description}</p>
                    <p className="text-sm text-gray-500">{activity.user}</p>
                  </div>
                  <div className="flex-shrink-0 text-sm text-gray-500">
                    {formatDate(activity.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">ยังไม่มีกิจกรรม</p>
              <p className="text-sm">กิจกรรมล่าสุดจะแสดงที่นี่</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;
